/**
 * Timeframes of the restaurant dashboard widgets (performance chart, top
 * products, heatmap), anchored on the branch business "today". All Dates are
 * local-midnight day carriers: a Date with hours > 0 would be serialized by
 * StatsService as yyyyMMddHH (explicit-hour mode, no business shift).
 */

export type TDashboardTimeframe =
  | 'today'
  | 'yesterday'
  | '7days'
  | '4weeks'
  | 'thisMonth';

/** Inclusive local-midnight day range. */
export interface IDayRange {
  from: Date;
  to: Date;
}

function addDays(day: Date, days: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + days);
}

function firstOfMonth(day: Date, monthOffset: number): Date {
  return new Date(day.getFullYear(), day.getMonth() + monthOffset, 1);
}

/** Same day-of-month `months` away, clamped to that month's last day. */
function shiftMonthClamped(day: Date, months: number): Date {
  const target = firstOfMonth(day, months);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0
  ).getDate();
  return new Date(
    target.getFullYear(),
    target.getMonth(),
    Math.min(day.getDate(), lastDay)
  );
}

/** yyyyMMdd key of a day (the format of stats `date` fields). */
export function toStatDayKey(day: Date): string {
  return (
    String(day.getFullYear()) +
    String(day.getMonth() + 1).padStart(2, '0') +
    String(day.getDate()).padStart(2, '0')
  );
}

/** Local-midnight Date of a yyyyMMdd key. */
export function parseStatDayKey(key: string): Date {
  return new Date(
    Number(key.slice(0, 4)),
    Number(key.slice(4, 6)) - 1,
    Number(key.slice(6, 8))
  );
}

/** Single-day timeframes the API answers with hourly buckets. */
export function isHourlyTimeframe(timeframe: TDashboardTimeframe): boolean {
  return timeframe === 'today' || timeframe === 'yesterday';
}

/**
 * Range of a timeframe. "7 días" and "4 semanas" end YESTERDAY (the
 * dashboard's long-standing semantics); "Este mes" runs from the 1st to
 * today.
 */
export function getDashboardRange(
  timeframe: TDashboardTimeframe,
  today: Date
): IDayRange {
  switch (timeframe) {
    case 'today':
      return { from: addDays(today, 0), to: addDays(today, 0) };
    case 'yesterday':
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case '7days':
      return { from: addDays(today, -7), to: addDays(today, -1) };
    case '4weeks':
      return { from: addDays(today, -28), to: addDays(today, -1) };
    case 'thisMonth':
      return { from: firstOfMonth(today, 0), to: addDays(today, 0) };
  }
}

/**
 * The comparison day of a current-period day, or null when it does not
 * exist (29-31 of a month whose previous month is shorter).
 */
export function getDashboardComparisonDay(
  timeframe: TDashboardTimeframe,
  day: Date
): Date | null {
  switch (timeframe) {
    case 'thisMonth': {
      const previous = shiftMonthClamped(day, -1);
      return previous.getDate() === day.getDate() ? previous : null;
    }
    case '4weeks':
      return addDays(day, -28);
    case 'today':
    case 'yesterday':
    case '7days':
      return addDays(day, -7);
  }
}

/** The previous period a timeframe is compared with. */
export function getDashboardComparisonRange(
  timeframe: TDashboardTimeframe,
  range: IDayRange
): IDayRange {
  switch (timeframe) {
    case 'thisMonth':
      return {
        from: firstOfMonth(range.from, -1),
        to: shiftMonthClamped(range.to, -1),
      };
    case '4weeks':
      return { from: addDays(range.from, -28), to: addDays(range.to, -28) };
    case 'today':
    case 'yesterday':
    case '7days':
      return { from: addDays(range.from, -7), to: addDays(range.to, -7) };
  }
}

/** Heatmap range: the `days` business days that end yesterday. */
export function getHeatmapRange(days: number, today: Date): IDayRange {
  return { from: addDays(today, -days), to: addDays(today, -1) };
}
